import React from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { formatDistanceToNow } from "date-fns";

const SalaryDetailModal = ({ show, onHide, salaryhistory }) => {
  if (!salaryhistory) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatAmount = (amount) =>
    Number(amount ?? 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const clientWiseData = salaryhistory.clientWiseData || [];

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Salary Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <h5 className="mb-3">Employee Information</h5>

        <p>
          <strong>Employee ID:</strong> {salaryhistory.id_employee || "-"}
        </p>
        <p>
          <strong>Name:</strong> {salaryhistory.id_user?.name || "N/A"}
        </p>
        <p>
          <strong>Staff Type:</strong>{" "}
          {salaryhistory.id_user?.id_generalStaffType?.name || "N/A"}
        </p>
        <p>
          <strong>Email:</strong> {salaryhistory.id_user?.email || "N/A"}
        </p>
        <p>
          <strong>Month/Year:</strong> {salaryhistory.month}/
          {salaryhistory.year}
        </p>

        <hr />

        <h5 className="mb-3">Attendance Details</h5>

        <p>
          <strong>Working Days:</strong> {salaryhistory.working_days ?? 0}
        </p>
        <p>
          <strong>Present Days:</strong> {salaryhistory.present_days ?? 0}
        </p>
        <p>
          <strong>Absent Days:</strong> {salaryhistory.absent_days ?? 0}
        </p>
        <p>
          <strong>Paid Days:</strong> {salaryhistory.paid_days ?? 0}
        </p>
        <p>
          <strong>Unpaid Leaves:</strong> {salaryhistory.unpaid_leaves ?? 0}
        </p>

        <hr />

        <h5 className="mb-3">Client-wise Salary & Trips</h5>

        {clientWiseData.length > 0 ? (
          <Table bordered responsive hover size="sm" className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Client</th>
                <th className="text-end">Gross Salary</th>
                <th className="text-end">Total Trips</th>
              </tr>
            </thead>

            <tbody>
              {clientWiseData.map((client, index) => {
                const clientName =
                  typeof client.id_client === "object"
                    ? client.id_client?.name
                    : null;

                return (
                  <tr key={client._id || client.id_client?._id || index}>
                    <td>{clientName || "Client name unavailable"}</td>
                    <td className="text-end">
                      ₹{formatAmount(client.gross_salary)}
                    </td>
                    <td className="text-end">
                      {Number(client.totalOrders ?? 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot className="table-light">
              <tr>
                <th>Overall Total</th>
                <th className="text-end">
                  ₹{formatAmount(salaryhistory.gross_salary)}
                </th>
                <th className="text-end">
                  {Number(salaryhistory.totalOrders ?? 0).toLocaleString(
                    "en-IN",
                  )}
                </th>
              </tr>
            </tfoot>
          </Table>
        ) : (
          <p className="text-muted">
            No client-wise breakdown available for this salary record.
          </p>
        )}

        <hr />

        <h5 className="mb-3">Salary & Deductions</h5>

        <p>
          <strong>Overall Gross Salary:</strong> ₹
          {formatAmount(salaryhistory.gross_salary)}
        </p>
        <p>
          <strong>Total Trips:</strong> {salaryhistory.totalOrders ?? 0}
        </p>
        <p>
          <strong>Advance Taken:</strong> ₹
          {formatAmount(salaryhistory.advanceMoney)}
        </p>

        <p>
          <strong>Cash Short:</strong> ₹{formatAmount(salaryhistory.cashSort)}
        </p>

        <p>
          <strong>Route Absentism:</strong> ₹
          {formatAmount(salaryhistory.routeabsentism)}
        </p>

        <p>
          <strong>Recoverable COD:</strong> ₹
          {formatAmount(salaryhistory.recoverablecod)}
        </p>

        <p>
          <strong>Pilferage:</strong> ₹{formatAmount(salaryhistory.philfridge)}
        </p>

        <p>
          <strong>Vehicle Challan:</strong>{" "}
          <span className="text-danger fw-semibold">
            ₹{formatAmount(salaryhistory.challanAmount)}
          </span>
        </p>

        <p>
          <strong>Total Deductions:</strong> ₹
          {formatAmount(salaryhistory.deductions)}
        </p>

        <p>
          <strong>Vehicle Damage Penalty:</strong> ₹
          {formatAmount(salaryhistory.vehicleDamagePenalty)}
        </p>
        <p>
          <strong>Pay After Deductions:</strong> ₹
          {formatAmount(salaryhistory.net_payable)}
        </p>

        <p>
          <strong>Final Salary:</strong> ₹
          {formatAmount(salaryhistory.actual_salary)}
        </p>
        <p>
          <strong>Is Final Salary Edited:</strong>{" "}
          {salaryhistory.isActualSalaryEdited ? "Yes" : "No"}
        </p>
        <hr />

        <h5 className="mb-3">Bank Details</h5>

        <p>
          <strong>Account Number:</strong>{" "}
          {salaryhistory.bank_details?.account_number || "-"}
        </p>
        <p>
          <strong>Bank Name:</strong>{" "}
          {salaryhistory.bank_details?.bank_name || "-"}
        </p>
        <p>
          <strong>Branch Name:</strong>{" "}
          {salaryhistory.bank_details?.branch_name || "-"}
        </p>
        <p>
          <strong>IFSC Code:</strong>{" "}
          {salaryhistory.bank_details?.ifsc_code || "-"}
        </p>
        <p>
          <strong>Account Holder Name:</strong>{" "}
          {salaryhistory.bank_details?.account_holder_name || "-"}
        </p>

        <hr />

        <h5 className="mb-3">Status & Audit</h5>

        <p>
          <strong>Is Finalized:</strong>{" "}
          {salaryhistory.isFinalized ? "Yes" : "No"}
        </p>
        <p>
          <strong>Is Paid:</strong> {salaryhistory.isPaid ? "Yes" : "No"}
        </p>
        <p>
          <strong>Notes:</strong> {salaryhistory.notes || "N/A"}
        </p>
        <p>
          <strong>Created At:</strong> {formatDate(salaryhistory.createdAt)}
        </p>
        <p>
          <strong>Updated At:</strong> {formatDate(salaryhistory.updatedAt)}
        </p>

        <hr />

        <div className="mt-4">
          <strong>Comments:</strong>

          {Array.isArray(salaryhistory.comments) &&
          salaryhistory.comments.length > 0 ? (
            <div className="mt-2 d-flex flex-column gap-2">
              {salaryhistory.comments.flatMap((comment, index) => {
                let parsedComments;

                try {
                  parsedComments = JSON.parse(comment.text);
                  if (!Array.isArray(parsedComments)) throw new Error();
                } catch {
                  parsedComments = [{ text: comment.text, date: comment.date }];
                }

                return parsedComments.map((c, subIndex) => {
                  const dateObj = c.date ? new Date(c.date) : null;
                  const validDate = dateObj && !Number.isNaN(dateObj.getTime());

                  return (
                    <div
                      key={`${comment._id || index}-${subIndex}`}
                      className="p-2 border rounded bg-light"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      <div>{c.text || "N/A"}</div>

                      {validDate && (
                        <div
                          className="text-muted mt-1"
                          style={{ fontSize: "0.8em" }}
                        >
                          {dateObj.toLocaleString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}

                          {" · "}

                          <span style={{ color: "red" }}>
                            Updated{" "}
                            {formatDistanceToNow(dateObj, {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                });
              })}
            </div>
          ) : (
            <p className="text-muted mt-2">No comments available</p>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SalaryDetailModal;
